/**
 * LocalizedLink Component
 *
 * A wrapper around React Router's Link that automatically
 * preserves the current locale prefix in navigation.
 */

import { Link, LinkProps, useParams, useNavigate, NavigateOptions } from 'react-router-dom';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/i18n';

interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  /** Force a specific locale instead of using current */
  locale?: SupportedLocale;
  /** If true, don't add locale prefix even for non-default locales */
  absolute?: boolean;
}

/**
 * Build a localized path with the appropriate locale prefix
 */
export function buildLocalizedPath(
  path: string,
  currentLocale: string | undefined,
  options?: { forceLocale?: SupportedLocale; absolute?: boolean }
): string {
  const { forceLocale, absolute } = options || {};

  // Determine target locale
  const targetLocale = forceLocale || currentLocale || DEFAULT_LOCALE;

  // If absolute, return path as-is
  if (absolute) {
    return path.startsWith('/') ? path : `/${path}`;
  }

  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  // Check if path already has a locale prefix
  const pathParts = cleanPath.split('/').filter(Boolean);
  const firstPart = pathParts[0];

  if (firstPart && SUPPORTED_LOCALES.includes(firstPart as SupportedLocale)) {
    // Path already has a locale prefix - respect forceLocale if provided
    if (forceLocale && forceLocale !== firstPart) {
      // Replace existing locale with forced one
      const restOfPath = '/' + pathParts.slice(1).join('/');
      return forceLocale === DEFAULT_LOCALE ? restOfPath : `/${forceLocale}${restOfPath}`;
    }
    return cleanPath;
  }

  // No existing locale prefix - add one if not default
  if (targetLocale === DEFAULT_LOCALE) {
    return cleanPath;
  }

  return `/${targetLocale}${cleanPath}`;
}

/**
 * LocalizedLink - automatically preserves locale in navigation
 *
 * @example
 * // Current URL: /es/dashboard
 * <LocalizedLink to="/pricing">Pricing</LocalizedLink>
 * // Renders link to: /es/pricing
 *
 * @example
 * // Force English regardless of current locale
 * <LocalizedLink to="/pricing" locale="en">English Pricing</LocalizedLink>
 * // Renders link to: /pricing (no prefix for default)
 */
export function LocalizedLink({
  to,
  locale,
  absolute,
  children,
  ...props
}: LocalizedLinkProps) {
  const { locale: currentLocale } = useParams<{ locale?: string }>();

  const localizedTo = buildLocalizedPath(to, currentLocale, {
    forceLocale: locale,
    absolute,
  });

  return (
    <Link to={localizedTo} {...props}>
      {children}
    </Link>
  );
}

/**
 * Hook for programmatic navigation with locale preservation
 *
 * @example
 * const navigate = useLocalizedNavigate();
 * navigate('/dashboard'); // Goes to /es/dashboard if currently on /es/*
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { locale: currentLocale } = useParams<{ locale?: string }>();

  return (
    to: string,
    options?: NavigateOptions & { locale?: SupportedLocale; absolute?: boolean }
  ) => {
    const { locale, absolute, ...navigateOptions } = options || {};

    const localizedPath = buildLocalizedPath(to, currentLocale, {
      forceLocale: locale,
      absolute,
    });

    navigate(localizedPath, navigateOptions);
  };
}

/**
 * Hook to get the current locale from URL
 */
export function useCurrentLocale(): SupportedLocale {
  const { locale } = useParams<{ locale?: string }>();

  if (locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale)) {
    return locale as SupportedLocale;
  }

  return DEFAULT_LOCALE;
}

export default LocalizedLink;
